/**
 * NajezdModal — the odometer series of one fleet car (per-row modal): the stock car's mileage, the readings
 * (newest first) and, at `pujcovna:write`, a form to record one. The newest reading also becomes the stock car's
 * mileage (the API writes it to the owner's row), which is what the Nájezd column of the fleet shows.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt, formatKm } from '@/lib/format';
import { pragueToday } from '@/lib/prague-date';
import { useAddOdecetMutation, useTachometrQuery, type Odecet } from './api';

const ZDROJ: Record<Odecet['zdroj'], string> = {
  rucni: 'Ruční odečet',
  korekce: 'Oprava',
  predani: 'Předání',
  vraceni: 'Vrácení',
};

const digits = (s: string) => s.replace(/\D/g, '');

interface NajezdModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NajezdModal({ id, title, canWrite, open, onOpenChange }: NajezdModalProps) {
  const query = useTachometrQuery(id);
  const add = useAddOdecetMutation(id);
  const [km, setKm] = useState('');
  const [datum, setDatum] = useState(pragueToday);
  const [poznamka, setPoznamka] = useState('');
  const [korekce, setKorekce] = useState(false);
  const busy = add.isPending;

  // Every way out ends in `onOpenChange(false)`; while the write is pending it would unmount the observer its
  // toasts hang on (the same rule as the other fleet modals), so it waits.
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  // The modal mounts per open: it renders only from a read that finished after the mount, never a cached snapshot.
  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const data = query.data;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!km) {
      toast.error('Vyplňte nájezd.');
      return;
    }
    add.mutate(
      { km: Number(km), datum, poznamka: poznamka.trim(), korekce },
      {
        onSuccess: (res) => {
          toast.ok(
            res.zapsano_do_skladu
              ? 'Odečet zapsán, nájezd vozidla ve skladu aktualizován.'
              : 'Odečet zapsán. Je starší než nájezd ve skladu, ten zůstal.',
          );
          setKm('');
          setPoznamka('');
          setKorekce(false);
        },
        onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se zapsat odečet.'}`),
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={720}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Nájezd <em>{title}</em>
          </>
        }
        sub="Odečty tachometru. Nejnovější odečet je i nájezd vozidla ve skladu."
      />
      <Modal.Body className="px-8 py-5">
        {query.isError ? (
          <p data-testid="najezd-error" className="text-[13px] text-ink-2">
            Odečty se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !fresh || !data ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div data-testid="najezd-sklad" className="flex flex-wrap items-baseline gap-x-3 text-[13px] text-ink-2">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Ve skladu</span>
              {data.sklad ? (
                <span>
                  <span className="font-mono text-ink">{formatKm(data.sklad.stav_km) ?? '—'}</span>
                  {data.sklad.datum_km && <span> · k {formatDate(data.sklad.datum_km)}</span>}
                </span>
              ) : (
                <span>záznam vozidla ve skladu teď není k dispozici</span>
              )}
            </div>

            {canWrite && (
              <form data-testid="najezd-form" onSubmit={submit} className="flex flex-col gap-4 border border-line p-4">
                <Fields cols={2}>
                  <Field label="Nájezd" required>
                    <TextInput
                      value={km}
                      inputMode="numeric"
                      suffix="km"
                      maxLength={7}
                      disabled={busy}
                      onChange={(e) => setKm(digits(e.currentTarget.value))}
                    />
                  </Field>
                  <Field label="Datum odečtu" required>
                    <TextInput
                      type="date"
                      value={datum}
                      max={pragueToday()}
                      disabled={busy}
                      onChange={(e) => setDatum(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="Poznámka" optional="volitelné" span>
                    <TextInput
                      value={poznamka}
                      maxLength={100}
                      disabled={busy}
                      onChange={(e) => setPoznamka(e.currentTarget.value)}
                    />
                  </Field>
                </Fields>
                <Checkbox
                  label="Oprava chybného údaje (smí být nižší než předchozí odečet)"
                  checked={korekce}
                  disabled={busy}
                  onChange={(e) => setKorekce(e.currentTarget.checked)}
                />
                <div>
                  <Button variant="ink" size="sm" type="submit" disabled={busy || !km || !datum}>
                    Zapsat odečet
                  </Button>
                </div>
              </form>
            )}

            {data.odecty.length === 0 ? (
              <p data-testid="najezd-prazdno" className="text-[13px] text-ink-2">
                Pro toto vozidlo zatím není zapsán žádný odečet.
              </p>
            ) : (
              <div data-testid="najezd-seznam" className="flex flex-col">
                <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Odečty · {data.odecty.length}
                </div>
                <ul className="border border-line">
                  {data.odecty.map((o) => (
                    <li key={o.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 border-b border-line px-3 py-2 last:border-b-0">
                      <span className="w-[96px] shrink-0 font-mono text-[12px] text-ink-2">{formatDate(o.datum)}</span>
                      <span className="w-[96px] shrink-0 text-right font-mono text-[13px] text-ink">{formatInt(o.km)} km</span>
                      <span className="shrink-0 text-[12px] text-ink-2">{ZDROJ[o.zdroj]}</span>
                      <span className="min-w-0 flex-1 truncate text-[12px] text-ink-2">
                        {[o.poznamka, o.jmeno].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
            Zavřít
          </Button>
        }
      />
    </Modal>
  );
}
